import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  useTheme,
  alpha,
  Tooltip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import AirIcon from '@mui/icons-material/Air';
import StarIcon from '@mui/icons-material/Star';
import DescriptionIcon from '@mui/icons-material/Description';
import Layout from '../components/layout/Layout';
import { useAuth } from '../context/AuthContext';
import {
  AirHeatExchangerIcon,
  ColdPlateIcon,
  CoaxialIcon,
  ShellAndTubeDryIcon,
  ShellAndTubeIcon,
  TubeComparisonIcon,
} from '../common/icons';
import { AppAccess, IconType } from '../types/appAccess';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user, appAccess, loading: authLoading } = useAuth();

  // Helper function to create consistent composite icons
  const createCompositeIcon = (baseIcon: React.ReactElement, overlayIcon: React.ReactElement) => (
    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {baseIcon}
      <Box sx={{
        position: 'absolute',
        top: '-8px',
        right: '-8px',
        zIndex: 2
      }}>
        {overlayIcon}
      </Box>
    </Box>
  );

  // Map icon types to actual React components
  const getIconComponent = (iconType: IconType, app: AppAccess) => {
    const iconProps = { sx: { fontSize: 64, color: theme.palette.primary.main } };
    
    switch (iconType) {
      case 'cold_plate':
        return <ColdPlateIcon {...iconProps} />;
      case 'shell_tube_evap':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <AcUnitIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'shell_tube_dry_evap':
        return createCompositeIcon(
          <ShellAndTubeDryIcon {...iconProps} />,
          <AcUnitIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'shell_tube_condenser':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <WaterDropIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'coaxial_evap':
        return createCompositeIcon(
          <CoaxialIcon {...iconProps} />,
          <AcUnitIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'coaxial_condenser':
        return createCompositeIcon(
          <CoaxialIcon {...iconProps} />,
          <WaterDropIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'shell_tube_special_evap':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <AcUnitIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'shell_tube_special_condenser':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <WaterDropIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'tube_comparison':
        return <TubeComparisonIcon {...iconProps} />;
      case 'expert_app':
        return createCompositeIcon(
          <ShellAndTubeIcon {...iconProps} />,
          <StarIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      case 'documents':
        return <DescriptionIcon {...iconProps} />;
      case 'air_heatexchanger':
        return createCompositeIcon(
          <AirHeatExchangerIcon {...iconProps} />,
          <AirIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
        );
      default:
        return <StarIcon {...iconProps} />;
    }
  };

  // Get current time for personalized greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Get dynamic welcome message
  const getWelcomeMessage = () => {
    const messages = [
      "Ready to design your next heat exchanger?",
      "Let's create something amazing today!",
      "Time to optimize your thermal solutions!",
      "Your next breakthrough design awaits!",
      "Let's push the boundaries of thermal efficiency!",
      "Ready to innovate in heat transfer?",
      "Your expertise makes a difference!",
      "Let's make thermal engineering easier!",
      "Time to revolutionize your heat exchanger design!",
      "Your next thermal solution is just a click away!",
      "Boost efficiency, reduce losses — start your calculation now!",
      "Precision matters: let’s model your heat exchanger performance!",
      "Turn complex heat transfer problems into simple solutions!",
      "Your pathway to maximum thermal efficiency starts here!",
      "Engineered for accuracy, designed for innovation!",
      "Optimize, simulate, and validate with confidence!",
      "Smarter calculations for smarter thermal systems!",
      "Heat transfer challenges? Let’s solve them together!",
      "From concept to optimization — streamline your workflow!",
      "Calculate, compare, and choose the best design instantly!",
      "Maximize heat recovery with the right design!",
      "Thermal efficiency is just a calculation away!",
      "Discover the best exchanger configuration in seconds!",
      "Advanced tools for advanced thermal engineering!",
      "Make every joule count — optimize your heat transfer!",
      "Your digital partner in exchanger efficiency!",
      "Turn data into performance improvements!",
      "Accelerate your design cycle with smarter tools!",
      "Stay ahead with next-generation heat transfer insights!",
      "Faster simulations, better decisions!",
      "Minimize energy loss, maximize sustainability!",
      "Achieve performance targets with confidence!",
      "Unlock new levels of exchanger efficiency!",
      "Bring clarity to complex thermal systems!",
      "Every calculation brings you closer to optimal design!"
    ];
    
    // Use the current date to select a message, ensuring it changes daily
    const today = new Date();
    const startOfYear = new Date(today.getFullYear(), 0, 0);
    const diff = today.getTime() - startOfYear.getTime();
    const dayOfYear = Math.floor(diff / (1000 * 60 * 60 * 24));
    return messages[dayOfYear % messages.length];
  };

  if (authLoading) {
    return (
      <Layout title="">
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '50vh',
            flexDirection: 'column',
            gap: 2
          }}
        >
          <CircularProgress size={48} />
          <Typography variant="h6" color="text.secondary">
            Loading your applications...
          </Typography>
        </Box>
      </Layout>
    );
  }


  if (!appAccess || appAccess.accessible_apps.length === 0) {
    return (
      <Layout title="">
        <Box sx={{ mb: 4 }}>
          <Alert severity="info">
            No applications are currently available to you. Please contact your administrator to request access.
          </Alert>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
        }}
      >
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.4, 0.0, 0.2, 1] }}
        >
          <Box 
            sx={{ 
              mb: { xs: 6, sm: 8 },
              textAlign: { xs: 'center', sm: 'left' },
              maxWidth: '1400px',
              mx: 'auto',
              px: { xs: 2, sm: 3 }
            }}
          >
            <Typography 
              variant="h3" 
              component="h1" 
              color="text.primary"
              sx={{ 
                fontWeight: 700,
                mb: 2,
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                letterSpacing: '-0.02em',
                lineHeight: 1.1
              }}
            >
              {getGreeting()}, {user?.first_name || 'there'}!
            </Typography>
            <Typography 
              variant="h6" 
              color="text.secondary"
              sx={{ 
                maxWidth: '600px',
                lineHeight: 1.6,
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 400,
                mx: { xs: 'auto', sm: 0 },
                mb: 1
              }}
            >
              {getWelcomeMessage()}
            </Typography>
            
            {/* App Access Summary */}
          </Box>
        </motion.div>

        {/* Apps Grid - Smartphone Homescreen Style */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
            lg: 'repeat(5, 1fr)',
            xl: 'repeat(6, 1fr)'
          },
          gap: { xs: 5, sm: 6, md: 7 },
          mb: 10,
          justifyItems: 'center',
          alignItems: 'start',
          maxWidth: '1400px',
          mx: 'auto',
          px: { xs: 2, sm: 3 }
        }}>
          <AnimatePresence>
            {appAccess.accessible_apps.map((app, index) => (
              <motion.div
                key={app.app_id}
                initial={{ opacity: 0, scale: 0.8, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ 
                  duration: 0.4,
                  delay: index * 0.08,
                  ease: [0.4, 0.0, 0.2, 1]
                }}
                whileHover={{ 
                  scale: 1.1,
                  y: -8,
                  transition: { duration: 0.2, ease: [0.4, 0.0, 0.2, 1] }
                }}
                whileTap={{ scale: 0.95 }}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0,
                    width: '100%',
                    maxWidth: { xs: 160, sm: 170, md: 180 }
                  }}
                >
                  {/* Hover Tooltip with App Details */}
                  <Tooltip
                    title={
                      <Box sx={{ p: 1 }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontSize: '1rem',
                            fontWeight: 600,
                            color: 'white',
                            textAlign: 'center',
                            mb: 1,
                            lineHeight: 1.2
                          }}
                        >
                          {app.app_name}
                        </Typography>
                        
                        <Typography
                          variant="body2"
                          sx={{
                            color: 'rgba(255, 255, 255, 0.9)',
                            textAlign: 'center',
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            maxWidth: 200
                          }}
                        >
                          {app.app_description}
                        </Typography>
                      </Box>
                    }
                    placement="top"
                    arrow
                    componentsProps={{
                      tooltip: {
                        sx: {
                          backgroundColor: theme.palette.mode === 'dark' 
                            ? 'rgba(0, 0, 0, 0.9)' 
                            : 'rgba(0, 0, 0, 0.9)',
                          backdropFilter: 'blur(10px)',
                          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                          borderRadius: 2,
                          maxWidth: 250
                        }
                      },
                      arrow: {
                        sx: {
                          color: theme.palette.mode === 'dark' 
                            ? 'rgba(0, 0, 0, 0.9)' 
                            : 'rgba(0, 0, 0, 0.9)'
                        }
                      }
                    }}
                  >
                    <Box
                      sx={{
                        width: { xs: 120, sm: 130, md: 140 },
                        height: { xs: 120, sm: 130, md: 140 },
                        borderRadius: { xs: 2, sm: 2.5, md: 3 },
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? alpha(theme.palette.primary.main, 0.15)
                          : '#f8f9fa',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out',
                        border: theme.palette.mode === 'dark' 
                          ? `2px solid ${alpha(theme.palette.primary.main, 0.3)}`
                          : `2px solid #e9ecef`,
                        boxShadow: theme.palette.mode === 'dark'
                          ? '0px 2px 8px rgba(0, 0, 0, 0.3)'
                          : '0px 2px 8px rgba(0, 0, 0, 0.1)',
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.25)
                            : '#ffffff',
                          borderColor: theme.palette.primary.main,
                          boxShadow: theme.palette.mode === 'dark'
                            ? '0px 4px 16px rgba(0, 0, 0, 0.4)'
                            : '0px 4px 16px rgba(44, 62, 80, 0.15)',
                          transform: 'translateY(-2px)',
                        }
                      }}
                      onClick={() => navigate(app.app_path)}
                    >
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'relative'
                      }}>
                        {/* Icon container */}
                        <Box sx={{ 
                          height: '70%',
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          width: '100%',
                          mb: 1
                        }}>
                          {getIconComponent(app.icon_type as IconType, app)}
                        </Box>
                        
                        {/* App Name at bottom */}
                        <Typography
                          variant="caption"
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            left: 0,
                            right: 0,
                            fontSize: { xs: '0.7rem', sm: '0.75rem' },
                            fontWeight: 600,
                            color: 'text.primary',
                            textAlign: 'center',
                            lineHeight: 1.2,
                            display: 'block',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            textShadow: theme.palette.mode === 'dark' 
                              ? '0px 1px 2px rgba(0, 0, 0, 0.8)' 
                              : '0px 1px 2px rgba(255, 255, 255, 0.8)'
                          }}
                        >
                          {app.app_name}
                        </Typography>
                      </Box>

                      {app.is_advanced && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            backgroundColor: theme.palette.warning.main,
                            color: theme.palette.warning.contrastText,
                            borderRadius: 1.5,
                            px: 0.75,
                            py: 0.25,
                            fontSize: '0.65rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            boxShadow: '0px 2px 4px rgba(0,0,0,0.2)',
                            border: `1px solid ${theme.palette.warning.dark}`,
                            zIndex: 2
                          }}
                        >
                          CUSTOM
                        </Box>
                      )}
                    </Box>
                  </Tooltip>
                </Box>
              </motion.div>
            ))}
          </AnimatePresence>
        </Box>
      </Box>
    </Layout>
  );
};

export default LandingPage;
